import { Component, Vue, Watch } from 'vue-property-decorator';
import { RegistrationSetup } from '@/interfaces/registration/RegistrationSetup';
import { RegistrationModel } from '@/interfaces/registration/models/RegistrationModel';
import { RegistrationData } from '@/interfaces/registration/RegistrationData';
import { RegistrationError } from '@/interfaces/registration/RegistrationError';
import lodash from 'lodash';
import api from '@/api/registration';
import { namespace } from 'vuex-class';
const registration = namespace('registration');
const navigation = namespace('navigation');

@Component
export default class RegistrationPanelMixin extends Vue {
  @registration.State
  public registrationSetup!: RegistrationSetup;

  @registration.State
  public registrationData!: RegistrationData;

  @registration.State
  public registrationModel!: RegistrationModel;

  @registration.Action
  public saveRegistrationModel!: (payload: { data: any; model: string }) => void;

  @registration.Action
  public saveRegistrationErrors!: (data: Array<RegistrationError>) => void;

  @navigation.State
  public savingData!: boolean;

  @navigation.State
  public validatingData!: boolean;

  @navigation.State
  public submitingData!: boolean;

  @navigation.Action
  public saveTabData!: () => void;

  @navigation.Action
  public resetTabData!: () => void;

  @navigation.Action
  public pauseNavigation!: () => void;

  @navigation.Action
  public resumeNavigation!: () => void;

  @Watch('savingData')
  onSavingDataChanged (newVal: boolean) {
    if (newVal) {
      this.validateData()
        .then(() => this.saveData())
        .then(() => this.submitFormData())
        .then(() => this.saveTabData())
        .catch((error) => {
          console.log(error);
          this.resetTabData();
        });
    }
  }

  public agreeToTermsLabel (): string {
    let label = 'I verify that I am at least ' + (this.registrationData.community?.ageRestriction === 'Under18' ? 18 : 13) + ' years old and agree to the ';
    if (this.registrationData.communityPreferences?.termsAndConditions !== undefined && this.registrationData.communityPreferences?.privacyPolicy !== undefined) {
      label = label + '<a href="/terms" target="_blank">' + this.registrationData.community?.name + ' terms and conditions</a> and <a href="/privacypolicy" target="_blank">privacy policy</a> and the ';
    } else if (this.registrationData.communityPreferences?.termsAndConditions !== undefined) {
      label = label + '<a href="/terms" target="_blank">' + this.registrationData.community?.name + ' terms and conditions</a> and the ';
    } else if (this.registrationData.communityPreferences?.privacyPolicy !== undefined) {
      label = label + '<a href="/privacypolicy" target="_blank">' + this.registrationData.community?.name + ' privacy policy</a> and the ';
    }
    label = label + '<a href="' + this.registrationData.communityPreferences?.termsAndConditionsUrl + '" target="_blank">' + this.registrationData.communityPreferences?.platformName + ' terms and conditions</a>';
    return label;
  }

  // Global validation
  public validateData (): Promise<boolean> {
    if (this.validatingData) {
      $('form').addClass('field-validating');
      if ($('.field-validation-error').length === 0) {
        return this.validatePanelData();
      } else {
        this.scrollToFirstError();
      }
      return Promise.reject(new Error('Validation errors'));
    }

    return Promise.resolve(true);
  }

  // Global save
  public saveData (): Promise<boolean> {
    $('form').removeClass('field-validating');
    return this.savePanelData();
  }

  // Override on panel to perform validation
  public validatePanelData (): Promise<boolean> {
    return Promise.resolve(true);
  }

  // Override on panel to save data
  public savePanelData (): Promise<boolean> {
    return Promise.resolve(true);
  }

  public getPanelData (model: string): any {
    return lodash.cloneDeep(lodash.get(this.registrationModel, model)) || {};
  }

  private createFormData (object: Record<string, any>, form?: FormData, namespace?: string): FormData {
    const formData = form || new FormData();
    for (const property in object) {
      if (!Object.prototype.hasOwnProperty.call(object, property) || !object[property]) {
        continue;
      }
      const formKey = namespace ? `${namespace}[${property}]` : property;
      if (object[property] instanceof Date) {
        formData.append(formKey, object[property].toISOString());
      } else if (typeof object[property] === 'object' && !(object[property] instanceof File)) {
        this.createFormData(object[property], formData, formKey);
      } else {
        formData.append(formKey, object[property]);
      }
    }
    return formData;
  }

  private scrollToFirstError (): void {
    const firstError = $('.field-validation-error').first();
    let scrollTopValue = 0;

    if (firstError.length > 0) {
      const formLabelParent = firstError.closest('.form-label-group');
      const formSwitchParent = firstError.closest('.form-switch');

      if (formLabelParent.length > 0) {
        scrollTopValue = formLabelParent.offset()!.top - 150;
      } else if (formSwitchParent.length > 0) {
        scrollTopValue = formSwitchParent.offset()!.top - 150;
      } else {
        scrollTopValue = firstError.offset()!.top - 200;
      }
    }

    $('html, body').animate({
      scrollTop: scrollTopValue
    }, 500);
  }

  public submitFormData (): Promise<boolean> {
    if (this.submitingData) {
      let url = '/';
      switch (this.registrationSetup.registrationType) {
        case 'Normal':
          url = '/account/register' + (this.registrationSetup.userGuid ? '/' + this.registrationSetup.userGuid : '');
          break;
        case 'IndividualMembership':
        case 'OrganizationMembership':
          url = '/account/register/member/' + this.registrationSetup.membershipId + (this.registrationSetup.userGuid ? '/' + this.registrationSetup.userGuid : '');
          break;
        case 'AttendeeRegistration':
          url = '/account/register/attendee/' + this.registrationSetup.registrationGuid;
          break;
        case 'ClaimGiftMembership':
        case 'OrganizationSeatInvite':
        case 'OrganizationAdminInvite':
        case 'VolunteerTeamLeaderInvite':
          url = 'account/acceptinvitation/' + this.registrationSetup.inviteGuid;
          break;
        case 'ClaimEcourse':
          url = 'account/acceptinvitation/' + this.registrationSetup.inviteGuid + '?membershipid=' + this.registrationSetup.membershipId;
          break;
        case 'VolunteerRegistration':
          url = 'account/register/volunteer/' + this.registrationSetup.volunteerEventSlug + '/' + this.registrationSetup.volunteerGuid;
          break;
        case 'CompleteProfile':
          url = 'account/completeprofile/' + this.registrationSetup.userGuid;
          break;
        case 'ExhibitorRegistration':
          url = 'account/register/exhibitor/' + this.registrationSetup.eventSlug + (this.registrationSetup.offerGuid ? '/' + this.registrationSetup.offerGuid : '');
          break;
      }

      if (this.registrationSetup.token) {
        url = url + (url.includes('?') ? '&' : '?') + 'token=' + this.registrationSetup.token;
      }

      if (this.registrationSetup.returnUrl) {
        url = url + (url.includes('?') ? '&' : '?') + 'returnUrl=' + this.registrationSetup.returnUrl;
      }

      const data: { [k: string]: any } = {
        User: {
          Id: this.registrationModel.login?.id,
          GUID: this.registrationModel.login?.guid,
          FirstName: this.registrationModel.personalInfo?.firstName,
          LastName: this.registrationModel.personalInfo?.lastName,
          Email: this.registrationData.accountCreationRequired ? this.registrationModel.login?.email : this.registrationModel.personalInfo?.email,
          Phone: this.registrationModel.personalInfo?.phone,
          BirthDate: this.registrationModel.personalInfo?.birthDate,
          Gender: this.registrationModel.personalInfo?.gender,
          Bio: this.registrationModel.personalInfo?.userBio,
          Twitter: this.registrationModel.personalInfo?.twitterHandle,
          Instagram: this.registrationModel.personalInfo?.instagramHandle
        },
        AddressForm: {
          Address: {
            Country: this.registrationModel.addressInfo?.address?.country,
            AddressLine1: this.registrationModel.addressInfo?.address?.addressLine1,
            AddressLine2: this.registrationModel.addressInfo?.address?.addressLine2,
            City: this.registrationModel.addressInfo?.address?.city,
            State: this.registrationModel.addressInfo?.address?.state,
            Zip: this.registrationModel.addressInfo?.address?.zip
          },
          DefaultHomeAddress: this.registrationModel.addressInfo?.defaultHomeAddress,
          DefaultShipAddress: this.registrationModel.addressInfo?.defaultShipAddress,
          DefaultBillAddress: this.registrationModel.addressInfo?.defaultBillAddress
        },
        AssociatedOrganization: {
          OrganizationId: this.registrationData.community?.organizationEnableAssociatedOrganizations ? this.registrationModel.personalInfo?.organizationId : 0,
          OrganizationName: this.registrationModel.personalInfo?.organizationName,
          Title: this.registrationModel.personalInfo?.organizationTitle
        },
        Password: this.registrationModel.login?.password,
        ConfirmPassword: this.registrationModel.login?.confirmPassword,
        UserAnonymityEnabled: this.registrationModel.login?.userAnonymity,
        UserAlias: this.registrationModel.login?.userAlias,
        UserAvatar: this.registrationModel.login?.userAvatar,
        UserReceiveNotificationEmailsPreference: this.registrationModel.login?.userReceiveNotificationEmails,
        ProfilePhotoFile: this.registrationModel.personalInfo?.profilePhotoFile,
        AgreeToTerms: this.registrationModel.login?.agreeToTerms || this.registrationModel.personalInfo?.agreeToTerms,
        AgreeToAttendeeTerms: this.registrationModel.personalInfo?.agreeToAttendeeTerms,
        EducationItems: lodash.map(this.registrationModel.education?.educationItems, (educationItem) => {
          return {
            Education: {
              Id: educationItem.id,
              OrganizationID: educationItem.organization?.id,
              Organization: {
                Name: educationItem.organization?.name
              },
              Relationship: educationItem.relationship,
              Degree: educationItem.degree,
              StartYear: educationItem.startYear,
              EndYear: educationItem.endYear
            },
            Delete: educationItem.delete
          };
        }),
        WorkHistoryItems: lodash.map(this.registrationModel.workHistory?.workHistoryItems, (workHistoryItem) => {
          return {
            WorkHistory: {
              Id: workHistoryItem.id,
              OrganizationID: workHistoryItem.organization?.id,
              Organization: {
                Name: workHistoryItem.organization?.name
              },
              Title: workHistoryItem.title,
              StartYear: workHistoryItem.startYear,
              EndYear: workHistoryItem.endYear
            },
            Delete: workHistoryItem.delete
          };
        }),
        dietType: this.registrationModel.eventMeals?.dietType,
        meals: lodash.split(this.registrationModel.eventMeals?.selectedMeals, ','),
        Privacy: this.registrationModel.login?.privacy,
        registrationType: this.registrationSetup.registrationType,
        onbehalfRegistrationGuid: this.registrationData.onbehalfRegistrationGuid
      };

      if (this.registrationModel.personalInfo?.birthDate !== undefined && this.registrationModel.personalInfo?.birthDate !== null) {
        const birthDate = new Date(this.registrationModel.personalInfo.birthDate);
        data.BirthDateYear = birthDate.getFullYear();
        data.BirthDateMonth = birthDate.getMonth() + 1;
        data.BirthDateDay = birthDate.getDate();
      }

      lodash.each(this.registrationModel.interests?.tagSetModels, (tagsetModel) => {
        if (tagsetModel.tags !== undefined && tagsetModel.tags.length > 0) {
          data[tagsetModel.name as string] = tagsetModel.tags.join(',');
        }
      });

      const formData = this.createFormData(data);

      lodash.each(this.registrationModel.questions, (questionModel) => {
        if (questionModel.value !== undefined && questionModel.value !== '') {
          formData.append('Answers[' + questionModel.id + '].Value', questionModel.value);
          formData.append('Answers[' + questionModel.id + '].FormQuestionID', questionModel.id?.toString() as string);
          formData.append('Answers.Index', questionModel.id?.toString() as string);
        }
      });

      lodash.each(this.registrationModel.volunteerQuestions, (questionModel) => {
        if (questionModel.value !== undefined && questionModel.value !== '') {
          formData.append('Answers[' + questionModel.id + '].Value', questionModel.value);
          formData.append('Answers[' + questionModel.id + '].FormQuestionID', questionModel.id?.toString() as string);
          formData.append('Answers.Index', questionModel.id?.toString() as string);
        }
      });

      lodash.each(this.registrationModel.eventQuestions, (questionModel) => {
        if (questionModel.value !== undefined && questionModel.value !== '') {
          formData.append('Answers[' + questionModel.id + '].Value', questionModel.value);
          formData.append('Answers[' + questionModel.id + '].FormQuestionID', questionModel.id?.toString() as string);
          formData.append('Answers.Index', questionModel.id?.toString() as string);
        }
      });

      if (this.registrationModel.reCaptchaResponse) {
        formData.append('g-recaptcha-response', this.registrationModel.reCaptchaResponse);
      }

      this.saveRegistrationErrors([]);

      return api
        .post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(response => {
          if (response.data.redirectUrl) {
            window.location.href = response.data.redirectUrl;
            return true;
          }

          const errors: Array<RegistrationError> = [];
          lodash.reduce(response.data, (result, value, key) => {
            if (value.errors.length > 0) {
              result.push({
                field: key,
                errors: lodash.map(value.errors, (error) => error.errorMessage)
              });
            }
            return result;
          }, errors);

          this.saveRegistrationErrors(errors);
          return false;
        })
        .catch(response => {
          this.saveRegistrationErrors([{
            field: 'All',
            errors: ['Failed to submit registration. Please try again later.']
          }]);
          return false;
        });
    }
    return Promise.resolve(true);
  }
}
